// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type AuthProvider = 'sso' | 'login';

type AuthUser = {
  email?: string;
  username?: string;
  name?: string;
  role?: string;
};

type StatusResponse = {
  isAuthenticated: boolean;
  user?: AuthUser | null;
};

type UseAuthStatusOptions = {
  authProvider: AuthProvider;
  redirectUri?: string;
  autoStart?: boolean;
};

type UseAuthStatusResult = {
  isAuthenticated: boolean;
  isAdmin: boolean;
  userRole: string;
  userEmail: string;
  authError: string | null;
  hasCheckedStatus: boolean;
  setAuthError: (message: string | null) => void;
  handleLoginSuccess: (user: AuthUser) => void;
  handleLoginFailure: (error: Error | string) => void;
};

const getUserDisplay = (user?: AuthUser | null): string => user?.email || user?.username || user?.name || '';

export const useAuthStatus = ({
  authProvider,
  redirectUri = '/',
  autoStart = false
}: UseAuthStatusOptions): UseAuthStatusResult => {
  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const [userEmail, setUserEmail] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);

  const handleLoginSuccess = useCallback((user: AuthUser): void => {
    setIsAuthenticated(true);
    setIsAdmin(user?.role === 'admin');
    setUserRole(user?.role ?? '');
    setUserEmail(getUserDisplay(user));
    setAuthError(null);
  }, []);

  const handleLoginFailure = useCallback((error: Error | string): void => {
    setIsAuthenticated(false);
    setIsAdmin(false);
    setUserRole('');
    setUserEmail('');
    setAuthError(typeof error === 'string' ? error : error.message);
  }, []);

  useEffect(() => {
    const checkLoginStatus = async (): Promise<void> => {
      try {
        const statusEndpoint = authProvider === 'sso' ? '/api/sso/status' : '/api/login/status';
        const response = await fetch(statusEndpoint, { credentials: 'include' });
        if (!response.ok) {
          throw new Error('Unable to verify login status.');
        }
        const data = (await response.json()) as StatusResponse;
        setIsAuthenticated(!!data.isAuthenticated);
        setUserEmail(getUserDisplay(data.user));
        setIsAdmin(data.user?.role === 'admin');
        setUserRole(data.user?.role ?? '');
      } catch (error) {
        setAuthError('Unable to verify login status. Please try again.');
        setIsAuthenticated(false);
        setUserEmail('');
        setIsAdmin(false);
        setUserRole('');
      } finally {
        setHasCheckedStatus(true);
      }
    };

    checkLoginStatus();
  }, [authProvider]);

  useEffect(() => {
    if (!autoStart || !hasCheckedStatus || isAuthenticated) {
      return;
    }
    if (authProvider === 'sso') {
      navigate(`/app/v1/login?redirectUri=${encodeURIComponent(redirectUri)}`);
    } else if (authProvider === 'login') {
      navigate(`/app/v2/login?redirectUri=${encodeURIComponent(redirectUri)}`);
    }
  }, [authProvider, autoStart, hasCheckedStatus, isAuthenticated, redirectUri, navigate]);

  useEffect(() => {
    const handler = (): void => {
      setAuthError('Session expired or unauthorized. Please log in again.');
      setIsAuthenticated(false);
      setUserEmail('');
      setIsAdmin(false);
      setUserRole('');
    };
    window.addEventListener('auth-unauthorized', handler);
    return () => window.removeEventListener('auth-unauthorized', handler);
  }, []);

  return {
    isAuthenticated,
    isAdmin,
    userRole,
    userEmail,
    authError,
    hasCheckedStatus,
    setAuthError,
    handleLoginSuccess,
    handleLoginFailure
  };
};
